import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./apprenticeship.css";

/* ------------ small reveal-on-scroll helper ------------ */
function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".ap-reveal"));
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

 {/* ===== REQUIREMENTS (single card) ===== */}
<section className="ap-req ap-reveal" aria-label="Apprenticeship details">
  <div className="container">
    <h3 className="ap-subtitle">Apprenticeship Details</h3>
    <div className="ap-req-body">
      <div className="ap-card">
        <h4 className="ap-card-title">Keep Building After Certification</h4>
        <p className="ap-card-text">
          All fundamental programs at COSMETINK® include the option to apprentice in our studio.
          After you receive your certification, simply opt in to the Apprenticeship Program to
          continue taking additional models in our space under mentorship.
        </p>
        <p className="ap-card-note">
          You’ll develop confidence, refine technique, and create publish-worthy before/afters
          with real-time coaching from our master trainers.
        </p>
      </div>
    </div>
  </div>
</section>

export default function Apprenticeship() {
  const heroVidRef = useRef(null);
  const [dimmer, setDimmer] = useState(0); // soften hero as user scrolls
  useReveal();

  /* one-time: scroll to top and wire a single scroll listener with cleanup */
  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => {
      const y = window.pageYOffset || 0;
      const op = Math.min(1, y / 220); // fade hero quickly but smoothly
      setDimmer(op);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* gentle playback */
  useEffect(() => {
    const v = heroVidRef.current;
    if (v) v.playbackRate = 0.8;
  }, []);

  return (
    <main className="ap-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          Permanent Makeup Apprenticeship | COSMETINK® Academy | Pennsylvania
        </title>
        <link rel="canonical" href="https://academy.cosmetink.com/apprenticeship" />
        <meta
          name="description"
          content="Apprentice at COSMETINK® after certification. Build your portfolio on real models with master-level mentorship. NJ AAM pathway supported."
        />
        <meta
          name="keywords"
          content="permanent makeup apprenticeship, microblading apprenticeship, ombre brows apprenticeship, lip blush apprenticeship, AAM 100 hour"
        />
      </Helmet>

      {/* ===== HERO ===== */}
      <section className="ap-hero">
        <div className="ap-media">
          <video
            ref={heroVidRef}
            className="ap-video"
            src="https://cosacademyvideos.s3.us-east-2.amazonaws.com/apprenticeshipvid.mp4"
            playsInline
            muted
            autoPlay
            loop
          />
        </div>
        <div
          className="ap-veil"
          style={{ opacity: 0.35 + dimmer * 0.4 }}
          aria-hidden
        />
        <header className="ap-hero-inner">
          <p className="ap-kicker">COSMETINK® Academy</p>
          <h1 className="ap-title">Apprenticeship Program</h1>
          <p className="ap-sub ap-reveal">
            Keep the momentum after certification: take additional models in our
            studio, build a portfolio that books clients, and grow with direct
            mentorship from our master trainers.
          </p>
        </header>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="ap-highlights container ap-reveal" aria-label="Program highlights">
        <div className="ap-pill">
          <div className="ap-pill__head">Real Studio Experience</div>
          <div className="ap-pill__body">
            Work inside a fully equipped, state-of-the-art PMU studio.
          </div>
        </div>
        <div className="ap-pill">
          <div className="ap-pill__head">Portfolio Models</div>
          <div className="ap-pill__body">
            Schedule additional models to refine technique & build proof.
          </div>
        </div>
        <div className="ap-pill">
          <div className="ap-pill__head">Ongoing Mentorship</div>
          <div className="ap-pill__body">
            Feedback, correction, and technique polishing from master artists.
          </div>
        </div>
      </section>


      {/* ===== HOW IT WORKS ===== */}
      <section className="ap-steps ap-reveal" aria-label="How the apprenticeship works">
        <div className="container">
          <h3 className="ap-subtitle">How It Works</h3>
          <ol className="ap-stepper">
            <li className="ap-step">
              <span className="ap-step__dot">1</span>
              <div>
                <h4 className="ap-step__title">Earn Your Certification</h4>
                <p className="ap-step__text">
                  Complete a COSMETINK® fundamentals program (Full Nano Brows, Nano Combo Brows, Ombré Brows, or Lip Blush).
                </p>
              </div>
            </li>
            <li className="ap-step">
              <span className="ap-step__dot">2</span>
              <div>
                <h4 className="ap-step__title">Opt In to Apprentice</h4>
                <p className="ap-step__text">
                  Tell us you’d like to apprentice—our team activates your in-studio access.
                </p>
              </div>
            </li>
            <li className="ap-step">
              <span className="ap-step__dot">3</span>
              <div>
                <h4 className="ap-step__title">Bring Models & Practice</h4>
                <p className="ap-step__text">
                  Book practice models, use our photo-ready space, and receive on-the-spot coaching.
                </p>
              </div>
            </li>
            <li className="ap-step">
              <span className="ap-step__dot">4</span>
              <div>
                <h4 className="ap-step__title">Document & Level Up</h4>
                <p className="ap-step__text">
                  We help track your progress and portfolio; NJ students also log models for AAM.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* ===== WHAT YOU GET ===== */}
      <section className="ap-includes ap-reveal" aria-label="What you get">
        <div className="container ap-grid">
          {[
            { h: "Mentored Sessions", p: "Hands-on oversight from master trainers with real-time adjustments." },
            { h: "Studio Access", p: "Work in a professional setting with proper lighting, setup, and photography." },
            { h: "Technique Refinement", p: "Color theory, mapping, needle selection, machine control—sharpened." },
            { h: "Portfolio Building", p: "Leave with publish-worthy before/afters that convert clients." },
          ].map((c, i) => (
            <div className="ap-inc" key={i}>
              <h4 className="ap-inc__title">{c.h}</h4>
              <p className="ap-inc__text">{c.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="ap-cta ap-reveal" aria-label="Get started">
        <div className="container ap-cta-inner">
          <h3 className="ap-cta-title">Ready to Apprentice?</h3>
          <p className="ap-cta-copy">
            Start with a fundamentals certification—then opt in to the Apprenticeship Program.
          </p>
          <div className="ap-cta-actions">
            <Link to="/courses/nano" className="ap-btn">Full Nano Brows</Link>
            <Link to="/courses/nanocombo" className="ap-btn">Nano Combo Brows</Link>
            <Link to="/courses/ombre" className="ap-btn">Ombré Brows</Link>
            <Link to="/courses/lips" className="ap-btn">Lip Blush</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
